import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Modal, Button, List, Header, Loader,
} from 'semantic-ui-react';
import ReviewModal from '../Meetings/ReviewModal';
import CustomLink from '../CustomLink';
import { JOB_QUALIFICATION_RESPONSES } from '../../queries/job';

const REGISTRATION_COUNTRIES = ['United States', 'France', 'Belgium', 'Netherlands'];
const REGISTRATION_TYPE_LABELS = { INAMI: 'INAMI/RIZIV' };
const NOT_COLLECTED = 'Not collected — registration IDs are collected only from experts in the United States, France, Belgium, and the Netherlands';
const NO_REGISTRATION = "I don't have a professional registration ID";

// An expert who ticked the opt-out box is stored with an empty registration list,
// which is only distinguishable from "never asked" by their country.
const registrationLines = ({ country, professionalRegistrations }) => {
  if (!REGISTRATION_COUNTRIES.includes(country)) return [NOT_COLLECTED];
  if (!professionalRegistrations?.length) return [NO_REGISTRATION];
  return professionalRegistrations.map(
    ({ type, number }) => `${REGISTRATION_TYPE_LABELS[type] ?? type}: ${number}`,
  );
};

const QualificationResponses = ({ children, job }) => {
  const [open, setOpen] = useState(false);
  // Full qualificationResponses (with `response` content) are not loaded by
  // PROJECT_JOBS to keep the list-view payload small. Fetch them on demand
  // when this modal opens; Apollo merges them into the same Job:id entity.
  const { loading, data } = useQuery(JOB_QUALIFICATION_RESPONSES, {
    skip: !open,
    variables: { id: job.id },
  });
  const responses = data?.job?.qualificationResponses ?? [];
  const requiresRegistration = data?.job?.project?.requiresProfessionalRegistration;
  return (
    <Modal
      closeIcon
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      trigger={children}
      id="qualification-responses"
    >
      <Modal.Header>{job.expert.fullName}</Modal.Header>
      <Modal.Content>
        {['Completed', 'Paid'].includes(job.overall_status) && (
          <p>
            Did you find the response to be accurate?
            <ReviewModal meeting={job}>
              <CustomLink>
                {job.ratingOnExpert || job.reviewOnExpert ? 'Edit Review' : 'Leave a Review'}
              </CustomLink>
            </ReviewModal>
          </p>
        )}
        {loading && <Loader active inline="centered" />}
        {!loading && (
          <List bulleted>
            {requiresRegistration && (
              <List.Item>
                <Header as="h4">Professional registration ID</Header>
                <List bulleted>
                  {registrationLines(data.job.expert).map((line) => (
                    <List.Item key={line}>{line}</List.Item>
                  ))}
                </List>
              </List.Item>
            )}
            {responses.map((r) => (
              <List.Item key={`${r.question}${new Date().getTime()}`}>
                <Header as="h4">
                  {r.question}
                </Header>
                {r.response.map((el) => (
                  <List bulleted key={`${el}${new Date().getTime()}`}>
                    <List.Item>
                      {el}
                    </List.Item>
                  </List>
                ))}
              </List.Item>
            ))}
          </List>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button className="primary-btn" onClick={() => setOpen(false)}>Close</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default QualificationResponses;
